import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { BrowserRouter, Routes, Route, useMatches, RouteObject } from 'react-router-dom';
import { RootieView } from '../../../_runtime/screens/RootieView';
import { useScreenPropsProvider } from './use-screen-props-provider.hook';
import { ScreenProps } from '../../../components-common/_types';

// Define the context type
interface ScreenContextType {
  currentScreenId: string | null;
  setCurrentScreenId: React.Dispatch<React.SetStateAction<string | null>>;
  getCurrentProps: () => ScreenProps|null;
}

// Create the context with a default value of null
const ScreenContext = createContext<ScreenContextType | null>(null);

// Provider component
export const ScreenProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [currentScreenId, setCurrentScreenId] = useState<string | null>(null);
  const screenPropsProvider = useScreenPropsProvider();

  const getCurrentProps = ():ScreenProps|null => {
    if(!currentScreenId) return null;
    const result = screenPropsProvider.get(currentScreenId!) || null;
    return result;
  }

  const value = {
    currentScreenId,
    getCurrentProps,
    setCurrentScreenId,
  };

  return (
    <ScreenContext.Provider value={value}>
      {children}
    </ScreenContext.Provider>
  );
};

// Custom hook to use the ScreenContext
const useScreenContext = (): ScreenContextType => {
  const context = useContext(ScreenContext);
  if (!context) {
    throw new Error('useScreenContext must be used within a ScreenProvider');
  }
  return context;
};


export const useScreen = () => {
  const screenContext = useScreenContext();
  const matches = useMatches();

  useEffect(() => {
    const currentMatch = matches.find(match => match.handle && (match.handle as any).screenId) as any;
    if (currentMatch) {
      screenContext.setCurrentScreenId(currentMatch.handle.screenId);
    }
  }, [matches]);
  return screenContext.getCurrentProps();
}
